import React, { useCallback, useContext, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import {
    selectArticleCommentInputStates,
    selectArticleCommentSendState,
    selectArticleCommentState
} from "../../store/knowledgeBaseComments/selectors"
import {
    cancelArticleCommentInput,
    cancelArticleCommentInputIfEmpty,
    enableArticleCommentInputMode,
    getArticleComments,
    sendArticleComment
} from "../../store/knowledgeBaseComments/thunks"
import { selectCurrentUser } from "../../store/users/selectors"
import { ArticleCommentInput } from "../../models/articleComment"
import { ArticleContext } from "../../pages/KnowledgeBase/KnowledgeBase"
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"
import ArticleCommentsView from "./ArticleCommentsView"
import { selectArticleExpanded } from "../../store/knowledgeBase/selectors"
import { testId } from "../../utility/tests/testId/knowledgeBaseComments"

interface Props {
    articleCode: string
    articleTitle: string
}

const ArticleCommentsViewContainer: React.FC<Props> = props => {
    const { articleCode, articleTitle } = props
    const dispatch = useAppDispatch()
    const articleCommentInputStates = useAppSelector(selectArticleCommentInputStates)
    const articleCommentSendState = useAppSelector(selectArticleCommentSendState)
    const articleCommentState = useAppSelector(selectArticleCommentState)
    const { shareId, projectId } = useContext(KnowledgeBaseContext)
    const user = useAppSelector(selectCurrentUser)
    const { onThreadOpen } = useContext(ArticleContext)
    const isArticleExpanded = useAppSelector(selectArticleExpanded)

    useEffect(() => {
        if (!shareId) {
            projectId && articleCode && dispatch(getArticleComments(projectId, articleCode))
        }

        return () => {
            articleCode && cancelArticleCommentInputIfEmpty(dispatch, articleCode)
        }
    }, [dispatch, projectId, articleCode, shareId])

    const handleEnableCommentInputMode = useCallback(
        (articleCode: string) => {
            enableArticleCommentInputMode(dispatch, articleCode)
        },
        [dispatch]
    )

    const handleCancelCommentInput = useCallback(
        (articleCode: string) => {
            cancelArticleCommentInput(dispatch, articleCode)
        },
        [dispatch]
    )

    const handleSendComment = useCallback(
        (value: ArticleCommentInput) => {
            projectId && dispatch(sendArticleComment(projectId, articleCode, articleTitle, value))
        },
        [projectId, dispatch, articleCode, articleTitle]
    )

    return (
        <ArticleCommentsView
            user={user}
            articleCode={articleCode}
            articleTitle={articleTitle}
            articleCommentState={articleCommentState}
            articleCommentSendState={articleCommentSendState}
            inputStates={articleCommentInputStates}
            onEnableInputMode={handleEnableCommentInputMode}
            onCancelInput={handleCancelCommentInput}
            onThreadOpen={onThreadOpen}
            onCommentSend={handleSendComment}
            isArticleExpanded={isArticleExpanded}
            dataTestId={testId.articleComments}
        />
    )
}

export default ArticleCommentsViewContainer
